import { usePublishedCollections } from 'marketplace-ui-common/collections/hooks/usePublishedCollections';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { useParams } from 'react-router-dom';
import { useUmbrellaCollections } from '../../collections/hooks/useUmbrellaCollections';
import { useAppContext } from '../../context/AppContext';
/** Fetches collection based on URL slug or most recent collection, with umbrella collection support */
export function useCollectionQuery(options = {
  mode: 'url'
}) {
  const {
    mode
  } = options;
  const appContext = useAppContext();
  const {
    collectionSlug
  } = useParams();
  const publishedCollections = usePublishedCollections({
    appContext,
    offeringType: 'APP'
  });
  const umbrellaCollections = useUmbrellaCollections();
  const umbrellaCollectionsRequestState = getDataFetchingClientRequestState(umbrellaCollections.error, umbrellaCollections.loading);

  // For URL mode without slug, return immediately (matches original behavior)
  if (mode === 'url' && !collectionSlug) {
    return {
      loading: false,
      data: undefined,
      error: undefined,
      isUmbrellaCollection: false
    };
  }

  // Check if we're loading either regular or umbrella collections
  if (publishedCollections.requestState === 'PENDING' || umbrellaCollectionsRequestState === 'PENDING') {
    return {
      loading: true,
      data: undefined,
      error: undefined,
      isUmbrellaCollection: false
    };
  }
  if (publishedCollections.requestState === 'FAILED') {
    return {
      loading: false,
      data: undefined,
      error: new Error('Failed to load collections'),
      isUmbrellaCollection: false
    };
  }
  if (umbrellaCollectionsRequestState === 'FAILED') {
    return {
      loading: false,
      data: undefined,
      error: new Error('Failed to load umbrella collections'),
      isUmbrellaCollection: false
    };
  }

  // Get collection based on mode - consolidating the helper function logic
  const publishedCollection = mode === 'recent' ? publishedCollections.getDisplayedCollections()[0] : mode === 'id' ? publishedCollections.getCollectionById(options.collectionId) : collectionSlug ? publishedCollections.getCollectionBySlug(collectionSlug) : undefined;
  if (publishedCollection) {
    return {
      loading: false,
      data: publishedCollection,
      error: undefined,
      isUmbrellaCollection: false
    };
  }

  // If no regular collection found and we're in URL mode, check for umbrella collections
  if (mode === 'url' && collectionSlug) {
    const umbrellaCollection = umbrellaCollections.getUmbrellaCollectionBySlug(collectionSlug);
    if (umbrellaCollection) {
      return {
        loading: false,
        data: umbrellaCollection,
        error: undefined,
        isUmbrellaCollection: true
      };
    }
  }

  // Neither regular nor umbrella collection found (or recent mode with no collections)
  return {
    loading: false,
    data: undefined,
    error: new Error(mode === 'recent' ? 'No collections available' : 'Collection not found'),
    isUmbrellaCollection: false
  };
}